/* eslint-disable prettier/prettier */
import React, {useCallback, useMemo, useState} from 'react';
import {Box, Button, Grid, IconButton, Modal, Typography} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import styles, {ORANGE_COLOR} from '../../styles';
import {Control, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Team} from '../../interfaces';
import TeamFilter from './TeamFilter';
import {SessionTypes} from '../../enums';
import SessionTypeFilter, { SessionTypeFilters } from './FilterComponents/SessionTypes';
import SessionDatesFilter from './FilterComponents/SessionDates';
import SessionMediaFilter from './FilterComponents/SessionMedia';
import PendingDataFilter from './FilterComponents/PendingData';

export interface PlayerSessionFilterForm {
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  } | null;
  teamIds: Record<string, boolean>;
  sessionTypes: SessionTypeFilters;
  sessionMedia: {
    hasVideo: boolean;
  };
  dataStatus: {
    pending: boolean;
    processed: boolean;
  };
}

interface Props {
  currentFilters?: PlayerSessionFilterForm;
  onFilterUpdate: (newFilters: PlayerSessionFilterForm) => void;
  teams?: Team[];
}

const defaultFilterValue: PlayerSessionFilterForm = {
  dateRange: null,
  teamIds: {},
  sessionTypes: {
    [SessionTypes.game]: false,
    [SessionTypes.practice]: false,
    [SessionTypes.skills]: false,
  },
  dataStatus: {
    pending: false,
    processed: false,
  },
  sessionMedia: {
    hasVideo: false,
  },
};

const PlayerSessionFilter: React.FC<Props> = ({
  currentFilters,
  onFilterUpdate,
  teams,
}) => {
  const {t} = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const {handleSubmit, reset, control, setValue} =
    useForm<PlayerSessionFilterForm>({
      defaultValues: currentFilters || defaultFilterValue,
    });

  const onApply = useCallback(
    (data: PlayerSessionFilterForm) => {
      onFilterUpdate(data);
      setShowModal(false);
    },
    [onFilterUpdate],
  );

  const onCancel = useCallback(() => {
    reset(currentFilters || defaultFilterValue);
    setShowModal(false);
  }, [currentFilters, reset]);

  const filterIconActive = useMemo(() => {
    if (!currentFilters) {
      return false;
    }
    if (Object.values(currentFilters.teamIds).some(val => val)) {
      return true;
    }
    if (currentFilters.dateRange?.startDate || currentFilters.dateRange?.endDate) {
      return true;
    }
    if (Object.values(currentFilters.sessionTypes).some(val => val)) {
      return true;
    }
    if (currentFilters.dataStatus.pending || currentFilters.dataStatus.processed) {
      return true;
    }
    if (currentFilters.sessionMedia.hasVideo) {
      return true;
    }
    return false;
  }, [currentFilters]);

  return (
    <>
      <Box sx={{display: 'flex', alignItems: 'flex-end'}}>
        <IconButton
          sx={
            filterIconActive
              ? {
                  color: ORANGE_COLOR,
                }
              : {}
          }
          onClick={() => setShowModal(true)}>
          <FilterAltIcon fontSize="large" />
        </IconButton>
      </Box>
      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
        }}>
        <form onSubmit={handleSubmit(onApply)} className="filterform">
          <Box sx={styles.modal}>
            <Typography variant="h3" sx={{mb: 2}}>
              {t('sessionFilter.label')}
            </Typography>
            <Box sx={{padding: 2}}>
              {!!teams?.length && (
                <Box sx={{mb: 2}}>
                  <Typography variant="h4" sx={{mb: 2}}>
                    {t('sessionFilter.teamName')}
                  </Typography>
                  <Box>
                    <TeamFilter
                      teams={teams}
                      control={control as unknown as Control<FieldValues>}
                      name="teamIds"
                    />
                  </Box>
                </Box>
              )}
              <SessionDatesFilter
                control={control as unknown as Control<FieldValues>}
                setValue={setValue}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <SessionTypeFilter
                    control={control as unknown as Control<FieldValues>}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <PendingDataFilter
                    control={control as unknown as Control<FieldValues>}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <SessionMediaFilter
                    control={control as unknown as Control<FieldValues>}
                  />
                </Grid>
              </Grid>
            </Box>
            <Box sx={{textAlign: 'right'}}>
              <Button sx={{mr: 2}} onClick={onCancel}>
                {t('general.cancel')}
              </Button>
              <Button variant="contained" type="submit">
                {t('general.apply')}
              </Button>
            </Box>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default PlayerSessionFilter;
